import { useState } from "react"
import { formatNumber } from "@/v2-common/utils/utils.formatting"
import cn from "@/v2-ui/utils/utils.cn"
import TooltipIcon from "@/v2-ui/tooltip/TooltipIcon"
import Badge from "@/v2-backoffice-client/components/badge"
import Button from "@/v2-backoffice-client/components/button"
import Link from "@/v2-backoffice-client/components/link"
import TooltipLink from "@/v2-backoffice-client/components/link/TooltipLink"
import {
  FENERUM_ACCOUNT_BASE_URL,
  HUBSPOT_BASE_URL,
} from "@/v2-backoffice-client/constants"
import UsageReportCard from "@/v2-backoffice-client/usage/report/UsageReportCard"
import UsageChurnRiskBadge from "@/v2-backoffice-client/usage/scope/UsageChurnRiskBadge"
import UsageSeatBar from "@/v2-backoffice-client/usage/scope/UsageSeatBar"
import UsageScoreMeter from "@/v2-backoffice-client/usage/UsageScoreMeter"
import UsageInsightText from "@/v2-backoffice-client/usage/UsageInsightText"
import UsageTrendBadge from "@/v2-backoffice-client/usage/UsageTrendBadge"
import { formatCompactAmount } from "@/v2-backoffice-client/customer/billing/customerBilling.utils"
import { INSIGHT_TONE_DOT_CLASSES } from "@/v2-backoffice-client/usage/usage.constants"
import {
  CHURN_LEVEL_CHART_COLORS,
  PLAY_KIND_LABELS,
  PLAYBOOK_COPY_FEEDBACK_MS,
  RENEWAL_SOON_DAYS,
} from "@/v2-backoffice-client/portfolio/portfolio.constants"
import {
  buildCustomerActivityLink,
  buildUserUsageLink,
  deadlineBadgeVariant,
  formatDeadline,
} from "@/v2-backoffice-client/portfolio/portfolio.utils"
import type { PortfolioPlayGroup } from "@/v2-backoffice-client/portfolio/portfolio.plays.utils"
import { formatPlaybookNote } from "@/v2-backoffice-client/portfolio/portfolio.playbook.utils"

export type PortfolioPlaybookCardProps = {
  group: PortfolioPlayGroup
}

// One account: who, how it stands, what to do. The churn level colours the
// left edge so a column of cards scans by risk. The standing is pictures,
// not sentences: the money, the seat bar, the engagement meter, the trend.
// Each play is one line — the move in bold and the data behind it — with
// the full explanation one hover away, so a chapter of forty cards can be
// read down the left edge. The copy tick is the card's own business.
function PortfolioPlaybookCard(props: PortfolioPlaybookCardProps) {
  const { group } = props
  const [copied, setCopied] = useState(false)

  // The play texts ARE the HubSpot note — copy them instead of retyping.
  function copyNote() {
    void navigator.clipboard.writeText(formatPlaybookNote(group))
    setCopied(true)
    window.setTimeout(() => setCopied(false), PLAYBOOK_COPY_FEEDBACK_MS)
  }

  return (
    <UsageReportCard
      className="border-l-2"
      style={{
        borderLeftColor:
          group.churnLevel === "none"
            ? CHURN_LEVEL_CHART_COLORS.new
            : CHURN_LEVEL_CHART_COLORS[group.churnLevel],
      }}
      title={
        <span className="flex flex-wrap items-center gap-2">
          <Link
            to={buildCustomerActivityLink(group.customerExternalId)}
            iconSrc={null}
          >
            {group.customerName}
          </Link>
          <UsageChurnRiskBadge
            level={group.churnLevel}
            reasons={group.churnReasons}
          />
          {group.deadline != null &&
            group.deadline.daysUntil <= RENEWAL_SOON_DAYS && (
              <Badge variant={deadlineBadgeVariant(group.deadline)}>
                {formatDeadline(group.deadline)}
              </Badge>
            )}
        </span>
      }
      aside={
        <>
          <Button
            variant="ghost"
            size="xs"
            iconSrc={copied ? "check" : "copy"}
            title="Copy the plays as text — paste into a HubSpot note or task"
            onClick={copyNote}
          />
          <TooltipLink
            variant="ghost"
            size="xs"
            iconSrc="hubspot"
            title="Open the company in HubSpot"
            external
            to={`${HUBSPOT_BASE_URL}/${group.customerExternalId}`}
          />
          {group.billingAccountUuid && (
            <TooltipLink
              variant="ghost"
              size="xs"
              iconSrc="bank"
              title="Open the billing account in Fenerum"
              external
              to={`${FENERUM_ACCOUNT_BASE_URL}/${group.billingAccountUuid}/`}
            />
          )}
        </>
      }
    >
      <div className="playbook-standing">
        <div
          className="playbook-standing-cell"
          title="Billed the last 12 months — the money this account's signals put at stake"
        >
          <span className="playbook-standing-value">
            {group.billed12M != null && group.billed12M > 0
              ? formatCompactAmount(group.billed12M, group.primaryCurrency)
              : "—"}
          </span>
          <span className="playbook-standing-label">billed 12 months</span>
        </div>
        <div
          className="playbook-standing-cell playbook-standing-cell--wide"
          title="Every seat by how engaged it was in the last 28 days; grey is idle"
        >
          <UsageSeatBar bands={group.seatBands} />
          <span className="playbook-standing-label">
            {formatNumber(group.activeSeats)} of {formatNumber(group.seatCount)}{" "}
            seats active
          </span>
        </div>
        <div
          className="playbook-standing-cell"
          title="Average engagement score of the active seats, out of 100"
        >
          <span className="playbook-standing-value">
            {group.avgScore != null ? Math.round(group.avgScore) : "—"}
          </span>
          <UsageScoreMeter score={group.avgScore} />
          <span className="playbook-standing-label">engagement</span>
        </div>
        <div className="playbook-standing-cell">
          <UsageTrendBadge trendPct={group.trendPct} />
          <span className="playbook-standing-label">vs four weeks before</span>
        </div>
      </div>

      <ul className="playbook-plays">
        {group.plays.map((play) => (
          <li key={play.kind} className="playbook-play">
            <span
              aria-hidden
              className={cn(
                "playbook-play-dot",
                INSIGHT_TONE_DOT_CLASSES[play.tone],
              )}
            />
            <span className="playbook-play-label">
              {PLAY_KIND_LABELS[play.kind]}
            </span>
            <span className="playbook-play-brief">
              <UsageInsightText
                text={play.brief}
                mentions={play.mentions}
                buildTo={(mention) => buildUserUsageLink(mention.userId)}
              />
            </span>
            <TooltipIcon
              iconSrc="info"
              iconSize={13}
              iconClassName="shrink-0 text-muted-foreground/60"
              content={
                <span className="block max-w-[320px] px-3 py-2 text-xs font-normal leading-relaxed">
                  {play.text}
                </span>
              }
              contentSide="top"
              contentClassName="ui-tooltip-content--panel"
            />
          </li>
        ))}
      </ul>
    </UsageReportCard>
  )
}

export default PortfolioPlaybookCard
